import { getNumericEnumValues, isNil } from "./utils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";

export function createTryParseNumericEnum<TEnum extends {}>(
	en: TEnum
): (value: string | nil) => TryResult<TEnum[keyof TEnum]> {
	return (value) => tryParseNumericEnum(value, en);
}

export function tryParseDate(
	value: string | nil
): TryResult<Date> {
	if (isNil(value)) {
		return {
			wasSuccessful: false
		};
	}

	const result: Date | null = parseDtoDate(value);

	if (!result || isNaN(result.getTime())) {
		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result
	};
}

export function tryParseDateOnly(
	value: string | nil
): TryResult<DateOnly> {
	if (isNil(value)) {
		return {
			wasSuccessful: false
		};
	}

	const result: DateOnly | null = parseDtoDateOnlyIgnoringOffset(value);

	if (!result) {
		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result
	};
}

export function tryParseBoolean(b: string | nil): TryResult<boolean> {
	if (isNil(b) || b === "") {
		return {
			wasSuccessful: false
		};
	}
	const normalizedValue: string = b.toUpperCase();
	const isTrue: boolean = normalizedValue === "TRUE"
		|| normalizedValue === "1";

	const isFalse: boolean = normalizedValue === "FALSE"
		|| normalizedValue === "0";

	if (isTrue) {
		return {
			wasSuccessful: true,
			result: true
		};
	}

	if (isFalse) {
		return {
			wasSuccessful: true,
			result: false
		};
	}

	return {
		wasSuccessful: false
	};
}

export function tryParseInt(value: string | nil): TryResult<number> {
	if (isNil(value)
		|| value === ""
		|| /^-?\d+$/.exec(value) === null) {
		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result: Number(value)
	};
}

export function tryParseFloat(value: string | nil): TryResult<number> {
	if (!value) {
		return {
			wasSuccessful: false
		};
	}

	const parsedValue: number = parseFloat(value);

	if (isNaN(parsedValue) || isNaN(value as unknown as number)) {
		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result: parsedValue
	};
}

export function tryParseNumericEnum<TEnum extends {}>(
	value: string | nil,
	en: TEnum
): TryResult<TEnum[keyof TEnum]> {
	const intParsing: TryResult<number> = tryParseInt(value);

	if (!intParsing.wasSuccessful) {
		return intParsing;
	}

	const valueDefinedOnEnum: TEnum[keyof TEnum] | undefined = getNumericEnumValues(en)
		.find(x => x === intParsing.result);

	if (valueDefinedOnEnum === undefined) {
		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result: valueDefinedOnEnum
	};
}
